<template>
  <div>
    <div class="page-header d-flex flex-wrap align-items-center justify-content-between mb-3">
      <h1 class="mb-2 mb-md-0">Machine File Review {{ machineSerial }}</h1>
      <div v-if="machineFile && userHasRole" class="d-flex align-items-center">
        <b-dropdown right variant="outline-secondary" text="Actions" size="sm">
          <b-dropdown-item @click="openReassignModal">
            <font-awesome-icon icon="exchange-alt" class="mr-1"/>
            Reassign Review
          </b-dropdown-item>
          <b-dropdown-item @click="openCreateMachineModal">
            <font-awesome-icon icon="plus" class="mr-1"/>
            Create New Machine
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item variant="danger" @click="openDiscardModal">
            <font-awesome-icon icon="trash-can" class="mr-1"/>
            Discard Review
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <b-alert v-if="saveErrorText" show dismissible variant="danger" class="mb-3" @dismissed="saveErrorText = null">
      {{ saveErrorText }}
    </b-alert>

    <div v-if="masterFile && machineFile">
      <machine-file-merger-overview :masterFile="masterFile" :machineFile="machineFile" @onChange="updateMergedFile"></machine-file-merger-overview>
      <div class="d-flex flex-wrap align-items-center justify-content-between my-4 pb-5" v-if="userHasRole">
        <span class="text-muted font-weight-bold">{{ allSelectedText }}</span>
        <div class="btn-group">
          <b-dropdown right variant="outline-secondary" text="Actions" size="sm" class="mr-2">
            <b-dropdown-item @click="openReassignModal">
              <font-awesome-icon icon="exchange-alt" class="mr-1"/>
              Reassign Review
            </b-dropdown-item>
            <b-dropdown-item @click="openCreateMachineModal">
              <font-awesome-icon icon="plus" class="mr-1"/>
              Create New Machine
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item variant="danger" @click="openDiscardModal">
              <font-awesome-icon icon="trash-can" class="mr-1"/>
              Discard Review
            </b-dropdown-item>
          </b-dropdown>
          <b-button :disabled="!allSelected || isSaving" @click="acceptReview" size="sm" variant="outline-dark" :title="!allSelected ? 'You need to fill out all sections' : ''">
            <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
            <font-awesome-icon v-else icon="check" class="mr-1"/>
            Save
          </b-button>
        </div>
      </div>
    </div>
    <div v-else-if="isMachineMissing" class="card mt-4 p-4 border-warning">
      <h4 class="text-warning">
        <font-awesome-icon icon="exclamation-circle" class="mr-2"/>
        Machine '{{ machineSerial }}' Not Found
      </h4>
      <p class="text-muted">
        The machine associated with this review does not exist in the database (it may have been renamed or deleted).
        You can resolve this review by creating a new machine, reassigning it to an existing machine, or discarding the review.
      </p>
      <div class="d-flex flex-wrap gap-2 mt-3" v-if="userHasRole">
        <b-button variant="success" class="mr-2 mb-2" @click="openCreateMachineModal">
          <font-awesome-icon icon="plus" class="mr-1"/>
          Create New Machine
        </b-button>
        <b-button variant="primary" class="mr-2 mb-2" @click="openReassignModal">
          <font-awesome-icon icon="exchange-alt" class="mr-1"/>
          Reassign to Existing Machine
        </b-button>
        <b-button variant="outline-danger" class="mb-2" @click="openDiscardModal">
          <font-awesome-icon icon="trash-can" class="mr-1"/>
          Discard Review
        </b-button>
      </div>
    </div>
    <div v-else class="text-center">
      <span v-if="errorText">{{ errorText }}</span>
      <b-spinner v-else label="Loading..." show variant="secondary"></b-spinner>
    </div>

    <!-- Reassign Modal -->
    <b-modal id="reassignModal" ref="reassignModal" title="Reassign Review" @ok.prevent="doReassign" :busy="actionBusy">
      <b-alert v-if="modalError" show variant="danger">{{ modalError }}</b-alert>
      <p>Reassign this review to another existing machine by entering its serial number:</p>
      <div class="form-group">
        <label for="targetSerialInput">Target Machine Serial Number</label>
        <div class="input-group">
          <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="barcode"/></span></div>
          <input id="targetSerialInput" v-model="reassignTargetSerial" class="form-control" style="text-transform:uppercase"
                 type="text" @input="reassignInput" @keyup.enter="doReassign">
        </div>
        <small class="form-text text-muted">Allowed: letters, numbers, spaces, dots, and hyphens.</small>
      </div>
      <template #modal-ok>
        <b-spinner v-if="actionBusy" small></b-spinner>
        <span v-else>Reassign</span>
      </template>
    </b-modal>

    <!-- Create Machine Modal -->
    <b-modal id="createMachineModal" ref="createMachineModal" title="Create Machine from Review" @ok.prevent="doCreateMachine" :busy="actionBusy">
      <b-alert v-if="modalError" show variant="danger">{{ modalError }}</b-alert>
      <p>Create a new machine using the data from this review:</p>
      <div class="form-group">
        <label for="newMachineSerial">Serial Number</label>
        <div class="input-group">
          <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="barcode"/></span></div>
          <input id="newMachineSerial" v-model="newMachineSerial" class="form-control" style="text-transform:uppercase"
                 type="text" @input="createMachineSerialInput" @keyup.enter="doCreateMachine">
        </div>
        <small class="form-text text-muted">Allowed: letters, numbers, spaces, dots, and hyphens.</small>
      </div>
      <div class="form-group">
        <label for="newMachineType">Machine Type</label>
        <div class="input-group">
          <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="tags"/></span></div>
          <input id="newMachineType" v-model="newMachineType" class="form-control" type="text" @keyup.enter="doCreateMachine">
        </div>
      </div>
      <div class="form-group">
        <label for="newMachineOwner">Owner / Customer Name</label>
        <div class="input-group">
          <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="briefcase"/></span></div>
          <input id="newMachineOwner" v-model="newMachineOwner" class="form-control" type="text" @keyup.enter="doCreateMachine">
        </div>
      </div>
      <template #modal-ok>
        <b-spinner v-if="actionBusy" small></b-spinner>
        <span v-else>Create Machine</span>
      </template>
    </b-modal>

    <!-- Discard Modal -->
    <b-modal id="discardModal" ref="discardModal" title="Discard Review" ok-variant="danger" ok-title="Discard" @ok.prevent="doDiscard" :busy="actionBusy">
      <b-alert v-if="modalError" show variant="danger">{{ modalError }}</b-alert>
      <p>Are you sure you want to discard this machine file review?</p>
      <p class="text-muted mb-0">It will be marked as reviewed without applying changes to any machine.</p>
      <template #modal-ok>
        <b-spinner v-if="actionBusy" small></b-spinner>
        <span v-else>Discard</span>
      </template>
    </b-modal>

    <b-toast ref="successToast" variant="success" fade no-title no-close-button auto-hide-delay="3000">
      {{ toastMessage }}
    </b-toast>
  </div>
</template>
<script>
import MachineFileMergerOverview from '@/components/machine_file/merger/Overview.vue'
import { machineFilesApi, machinesApi } from '@/backend'
import { machineFileWithDefaults } from '@/utils/MachineFile'
import { EditMachineQuery } from '@/screencheck_portal_api/src'
import { DateTime } from 'luxon'

export default {
  name: 'MachineFileReview',
  components: {
    MachineFileMergerOverview
  },
  data () {
    return {
      masterFile: null,
      machineFile: null,
      mergedFile: null,
      errorText: null,
      saveErrorText: null,
      isMachineMissing: false,
      allSelectedInSections: [],
      actionBusy: false,
      isSaving: false,
      modalError: null,
      toastMessage: 'Machine File saved successfully.',
      reassignTargetSerial: '',
      newMachineSerial: '',
      newMachineType: '',
      newMachineOwner: ''
    }
  },
  mounted () {
    this.fetchMachineFileForReview()
  },
  computed: {
    allSelectedText: function () {
      return this.allSelectedInSections.filter(x => x).length + ' of ' + this.allSelectedInSections.length + ' sections completed'
    },
    allSelected: function () {
      return this.allSelectedInSections.filter(x => x).length === this.allSelectedInSections.length
    },
    machineSerial: function () {
      return this.$route.params.serial
    },
    machineFileUuid: function () {
      return this.$route.params.uuid
    },
    userHasRole: function () {
      return this.$store.getters.isUserAdmin || this.$store.getters.isUserEngineeringManager
    },
  },
  watch: {
    $route: {
      handler: function (to) {
        if (to.name === 'machine-file-review' && this.$route.params.serial !== undefined && this.$route.params.uuid !== undefined) {
          this.fetchMachineFileForReview()
        }
      }
    }
  },
  methods: {
    fetchMachineFileForReview: function () {
      this.masterFile = null
      this.machineFile = null
      this.mergedFile = null
      this.isMachineMissing = false
      this.errorText = null

      machineFilesApi.getMachineFileOpenForReview(this.machineSerial, this.machineFileUuid, (error, reviewData) => {
        if (error) {
          this.errorText = 'Error fetching machine file for review.'
          console.error('Failed fetching machine files because: ' + error)
          return
        }
        this.machineFile = machineFileWithDefaults(this.machineSerial, reviewData)
        this.errorText = null

        machinesApi.getMachineFileDetails(this.machineSerial, (error, masterData) => {
          if (error) {
            this.isMachineMissing = true
            this.errorText = null
            return
          }
          this.isMachineMissing = false
          this.masterFile = machineFileWithDefaults(this.machineSerial, masterData)
          this.mergedFile = machineFileWithDefaults(this.machineSerial, masterData)
          this.errorText = null
        })
      })
    },
    acceptReview: function () {
      this.isSaving = true
      this.saveErrorText = null
      const targetSerial = this.mergedFile?.static?.machineSerialNumber?.trim()?.toUpperCase() || this.machineSerial.trim().toUpperCase()
      if (this.mergedFile?.static) {
        this.mergedFile.static.machineSerialNumber = targetSerial
      }

      if (JSON.stringify(this.masterFile) !== JSON.stringify(this.mergedFile)) {
        if (this.machineFile) {
          this.machineFile.timestamp = new Date()
        }
        let editMachineQuery = EditMachineQuery.constructFromObject({
          serial: targetSerial,
          type: this.mergedFile?.static?.machineType,
          owner: this.mergedFile?.static?.customerName,
          data: this.mergedFile
        }, undefined)
        editMachineQuery.data.timestamp = DateTime.now().toString()
        machinesApi.editMachine(this.machineSerial, editMachineQuery, (error) => {
          if (error) {
            this.isSaving = false
            this.saveErrorText = 'Could not update master machine file: ' + (error.response?.body?.message || error.message || error)
            console.error('Could not update master machine file: ' + (error.response?.body?.message || error.message || error))
            return
          }
          this.masterFile = this.mergedFile
          this.saveErrorText = null
          this.markMachineFileAsReviewed()
        })
      } else {
        this.markMachineFileAsReviewed()
      }
    },
    markMachineFileAsReviewed: function () {
      machineFilesApi.markMachineFileAsReviewed(this.machineFileUuid, { 'body': {} }, (error) => {
        this.isSaving = false
        if (error) {
          this.saveErrorText = 'Could not mark machine file as reviewed: ' + (error.response?.body?.message || error.message || error)
          console.error('Could not mark machine file as reviewed because: ' + error)
          return
        }
        this.toastMessage = 'Machine File saved successfully.'
        this.$refs.successToast.show()
        const targetSerial = this.mergedFile?.static?.machineSerialNumber?.trim()?.toUpperCase() || this.machineSerial
        this.$router.push('/machine/' + encodeURIComponent(targetSerial))
      })
    },
    updateMergedFile: function (updatedMergedFile, allSelectedInSections) {
      this.mergedFile = updatedMergedFile
      this.allSelectedInSections = allSelectedInSections
    },
    openReassignModal: function () {
      this.reassignTargetSerial = ''
      this.modalError = null
      this.$refs.reassignModal.show()
    },
    reassignInput: function () {
      this.reassignTargetSerial = this.reassignTargetSerial.toUpperCase().replace(/[^A-Z0-9 .-]/g, '')
    },
    doReassign: function () {
      const target = this.reassignTargetSerial.trim().toUpperCase()
      if (!target) {
        this.modalError = 'Please enter a target serial number.'
        return
      }
      this.actionBusy = true
      this.modalError = null
      machineFilesApi.reassignMachineFileReview(this.machineFileUuid, { targetSerial: target }, (error, data) => {
        this.actionBusy = false
        if (error) {
          this.modalError = error.response?.body?.message || 'Error reassigning review.'
          return
        }
        this.$refs.reassignModal.hide()
        const targetSerial = data?.targetSerial || target
        this.$router.push({
          name: 'machine-file-review',
          params: { serial: targetSerial, uuid: this.machineFileUuid }
        }).catch((err) => {
          if (err && err.name !== 'NavigationDuplicated') {
            console.error(err)
          }
        })
        if (targetSerial === this.machineSerial) {
          this.fetchMachineFileForReview()
        }
      })
    },
    openCreateMachineModal: function () {
      this.newMachineSerial = (this.machineFile?.static?.machineSerialNumber || this.machineSerial || '').trim().toUpperCase()
      this.newMachineType = this.machineFile?.static?.machineType || ''
      this.newMachineOwner = this.machineFile?.static?.customerName || ''
      this.modalError = null
      this.$refs.createMachineModal.show()
    },
    createMachineSerialInput: function () {
      this.newMachineSerial = this.newMachineSerial.toUpperCase().replace(/[^A-Z0-9 .-]/g, '')
    },
    doCreateMachine: function () {
      const serial = this.newMachineSerial.trim().toUpperCase()
      if (!serial) {
        this.modalError = 'Please enter a serial number.'
        return
      }
      this.actionBusy = true
      this.modalError = null
      machineFilesApi.createMachineFromReview(this.machineFileUuid, {
        serial: serial,
        type: this.newMachineType,
        owner: this.newMachineOwner
      }, (error) => {
        this.actionBusy = false
        if (error) {
          this.modalError = error.response?.body?.message || 'Error creating machine.'
          return
        }
        this.$refs.createMachineModal.hide()
        this.toastMessage = 'Machine created and review accepted.'
        this.$refs.successToast.show()
        this.$router.push('/machine/' + encodeURIComponent(serial))
      })
    },
    openDiscardModal: function () {
      this.modalError = null
      this.$refs.discardModal.show()
    },
    doDiscard: function () {
      this.actionBusy = true
      this.modalError = null
      machineFilesApi.discardMachineFileReview(this.machineFileUuid, {}, (error) => {
        this.actionBusy = false
        if (error) {
          this.modalError = error.response?.body?.message || 'Error discarding review.'
          return
        }
        this.$refs.discardModal.hide()
        this.toastMessage = 'Review discarded.'
        this.$refs.successToast.show()
        this.$router.push('/machines')
      })
    }
  }
}
</script>
